/**
 * PRP-WEB-1 W3.3 / DD6: feet-inches entry, stored as decimal feet like the phone.
 *
 * Accepts `12' 6"`, `12'6"`, `12'6`, `12' 6`, `12 6` (feet then inches), `12.5`, `12`, `12'`,
 * `6"` (inches only), `12ft 6in`, and the typographic marks ’ ” ′ ″. Inches may be decimal
 * (`12' 6.5"`). Returns null for anything else, a negative value, or inches of 12 or more
 * after a feet part.
 */
const NUM = String.raw`(\d+(?:\.\d+)?|\.\d+)`;
const FT = String.raw`(?:'|’|′|ft|feet|foot)`;
const IN = String.raw`(?:"|”|″|''|in|inch|inches)`;

const FEET_INCHES = new RegExp(String.raw`^${NUM}\s*${FT}?\s*-?\s*${NUM}\s*${IN}?$`, 'i');
const FEET_ONLY = new RegExp(String.raw`^${NUM}\s*${FT}?$`, 'i');
const INCHES_ONLY = new RegExp(String.raw`^${NUM}\s*${IN}$`, 'i');

export function parseFeetInches(text: string): number | null {
  const s = text.trim().replace(/\s+/g, ' ');
  if (!s) return null;
  let m = s.match(INCHES_ONLY);
  if (m) return round4(Number(m[1]) / 12);
  m = s.match(FEET_ONLY);
  if (m) return round4(Number(m[1]));
  m = s.match(FEET_INCHES);
  if (m) {
    const ft = Number(m[1]), inch = Number(m[2]);
    // "12.5 6" is not a reading; feet with a fraction take no inches part.
    if (!Number.isInteger(ft) || inch >= 12) return null;
    return round4(ft + inch / 12);
  }
  return null;
}

const round4 = (v: number) => Math.round(v * 1e4) / 1e4;

/** Decimal feet as the panel shows them: `12' 6"`, `12'`, or `0' 6"`, to the nearest inch. */
export function formatFeetInches(ft: number): string {
  const inches = Math.round(ft * 12);
  const f = Math.floor(inches / 12), i = inches - f * 12;
  return i ? `${f}' ${i}"` : `${f}'`;
}
